import GraphGroup from "./graph_group";
import GraphRect from "./graph_rect";
import GraphContainer, { getOrSet } from "./graph_container";
import createTextFilter from "./create-text-filter";

class GraphGridlines extends GraphRect {
    _showAxisX;
    _showAxisY;
    _showGrid;
    _colorBackground;
    _colorAxes;
    _colorMajor;
    _colorMinor;
    _showAxisArrowX;
    _showAxisArrowY;
    _axisWidth;
    _colorText;
    _colorTextShadow;
    _gridType;

    constructor(xax, yax, xrange, yrange) {
        super();

        this._axes = Object.freeze({ x: xax, y: yax });
        this._ranges = Object.freeze({ x: xrange, y: yrange });

        const { feFlood, filter } = createTextFilter(this._uuid);

        this._feFlood = feFlood;
        this._filter = filter;

        this._defs.append(this._filter);

        this._patternGridMajor = GraphContainer
            .create("pattern")
            .setAttribute("id", `grid_major_${this._uuid}`)
            .setAttribute("patternUnits", "userSpaceOnUse");
        this._patternGridMinor = GraphContainer
            .create("pattern")
            .setAttribute("id", `grid_minor_${this._uuid}`)
            .setAttribute("patternUnits", "userSpaceOnUse");

        this._gridMajor = GraphContainer
            .create("path")
            .setAttribute("stroke-width", 1.5)
            .setAttribute("fill", `url(#grid_minor_${this._uuid})`);
        this._gridMinor = GraphContainer
            .create("path")
            .setAttribute("stroke-width", 0.5);

        this._patternGridMajor.append(this._gridMajor);
        this._patternGridMinor.append(this._gridMinor);
        this._defs.append(this._patternGridMajor, this._patternGridMinor);

        this._lines = new GraphGroup()
            .setAttribute("name", "grid-lines")
            .setAttribute("clip-path", `url(#clip_${this._uuid})`);

        this.append(this._lines);

        this._step = 1;
        this._stepMinor = 0.25;
    }

    /**
     * @returns {this}
     */
    step() { return getOrSet.call(this, this, "_step", ...arguments); }

    /**
     * @returns {this}
     */
    stepMinor() { return getOrSet.call(this, this, "_stepMinor", ...arguments); }

    /**
     * @returns {this}
     */
    axisWidth() { return getOrSet.call(this, this, "_axisWidth", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisArrowX() { return getOrSet.call(this, this, "_showAxisArrowX", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisArrowY() { return getOrSet.call(this, this, "_showAxisArrowY", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisX() { return getOrSet.call(this, this, "_showAxisX", ...arguments); }

    /**
     * @returns {this}
     */
    gridType() { return getOrSet.call(this, this, "_gridType", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisY() { return getOrSet.call(this, this, "_showAxisY", ...arguments); }

    /**
     * @returns {this}
     */
    showGrid() { return getOrSet.call(this, this, "_showGrid", ...arguments); }

    /**
     * @returns {this}
     */
    colorBackground() { return getOrSet.call(this, this, "_colorBackground", ...arguments); }
    /**
     * @returns {this}
     */
    colorAxes() { return getOrSet.call(this, this, "_colorAxes", ...arguments); }
    /**
     * @returns {this}
     */
    colorMajor() { return getOrSet.call(this, this, "_colorMajor", ...arguments); }
    /**
     * @returns {this}
     */
    colorMinor() { return getOrSet.call(this, this, "_colorMinor", ...arguments); }

    /**
     * @returns {this}
     */
    colorText() { return getOrSet.call(this, this, "_colorText", ...arguments); }

    /**
     * @returns {this}
     */
    colorTextShadow() { return getOrSet.call(this, this, "_colorTextShadow", ...arguments); }


    redraw() {
        super.redraw();

        this._gridMajor.setAttribute("stroke", this._colorMajor);

        if (this._gridType !== "unit") {
            this._gridMinor
                .removeAttribute("display")
                .setAttribute("stroke", this._colorMinor)
                .setAttribute("fill", this._colorBackground);
        } else {
            this._gridMinor
                .setAttribute("display", "none");
        }

        this._feFlood.setAttribute("flood-color", this._colorTextShadow);
        this._lines.erase();

        const xrange = this._ranges.x, yrange = this._ranges.y;
        const step = this._gridType !== "unit" ? this._step : 1, stepMinor = this._stepMinor;
        const xfun = this._axes.x.value, yfun = this._axes.y.value;

        const clampX = x => Math.min(this._origin.x + this._size.width, Math.max(this._origin.x, x));
        const clampY = y => Math.min(this._origin.y + this._size.height, Math.max(this._origin.y, y));

        if (this._showGrid) {
            this._rect.setAttribute("fill", `url(#grid_major_${this._uuid})`);

            const setGridPattern = (pattern, grid, step) => {
                const [ox, x1, x2] = [xfun(xrange.x - (xrange.x % step)), xrange.x, xrange.x + step]
                    .map(val => THREE.Math.mapLinear(val, xrange.x, xrange.y, 0, this._size.width));
                const [oy, y1, y2] = [yfun(yrange.x - (yrange.x % step)), yrange.x, yrange.x + step]
                    .map(val => THREE.Math.mapLinear(val, yrange.x, yrange.y, 0, this._size.height));

                const w = x2 - x1, h = y2 - y1;

                pattern
                    .setAttribute("x", ox + this._origin.x)
                    .setAttribute("y", oy + this._origin.y)
                    .setAttribute("width", w)
                    .setAttribute("height", h);

                grid.setAttribute("d", `M${w} ${0} L${w} ${h} ${0} ${h} ${0} ${0}`);

                return pattern;
            }

            setGridPattern(this._patternGridMajor, this._gridMajor, step);

            if (this._gridType !== "unit") {
                setGridPattern(this._patternGridMinor, this._gridMinor, stepMinor)
                    .setAttribute("x", 0)
                    .setAttribute("y", 0);
            }
        }

        const strokeWidth = this._axisWidth;
        const strokeWidth2 = strokeWidth * 2, strokeWidth4 = strokeWidth2 * 2;
        const strokeWidth6 = strokeWidth2 + strokeWidth4, strokeWidth8 = strokeWidth4 * 2;

        if (this._showAxisX) {
            const yval = THREE.Math.mapLinear(0, yrange.x, yrange.y, 0, this._size.height);

            const x1 = clampX(this._origin.x), x2 = clampX(this._origin.x + this._size.width);
            const y = clampY(yval + this._origin.y);


            const hasRight = this._showAxisArrowX === "both" || this._showAxisArrowX === "right";
            const hasLeft = this._showAxisArrowX === "both" || this._showAxisArrowX === "left";

            this._lines.append(
                GraphContainer
                    .create("line")
                    .setAttribute("x1", clampX(x1 + (hasLeft ? strokeWidth4 : 0)))
                    .setAttribute("y1", y)
                    .setAttribute("x2", clampX(x2 - (hasRight ? strokeWidth4 : 0)))
                    .setAttribute("y2", y)
                    .setAttribute("stroke", this._colorAxes)
                    .setAttribute("name", "axis-x")
                    .setAttribute("stroke-width", strokeWidth)
                    .setAttribute("pointer-events", "none")
            );

            if (hasRight) {
                const _x = clampX(x2 - strokeWidth4);
                const _y = clampY(y - strokeWidth2);

                this._lines.append(
                    GraphContainer
                        .create("path")
                        .setAttribute("d", `M${_x} ${_y} L${x2} ${y} L${_x} ${clampY(y + strokeWidth2)} L${_x} ${_y}`)
                        .setAttribute("stroke", "none")
                        .setAttribute("fill", this._colorAxes)
                        .setAttribute("name", "chevron-x-right")
                );

                if (xrange.y >= 1) {
                    this._lines.append(
                        GraphContainer
                            .create("text")
                            .setAttribute("fill", this._colorText)
                            .setAttribute("name", "label-axis-px")
                            .setAttribute("text-anchor", "end")
                            .setAttribute("dominant-baseline", "central")
                            .setAttribute("x", _x)
                            .setAttribute("y", clampY(_y - strokeWidth8))
                            .setAttribute("font-size", strokeWidth8)
                            .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                            .innerText("X")
                    );
                }
            }

            if (hasLeft) {
                const _x = clampX(x1 + strokeWidth4);
                const _y = clampY(y - strokeWidth2);

                this._lines.append(
                    GraphContainer
                        .create("path")
                        .setAttribute("d", `M${x1} ${y} L${_x} ${_y} L${_x} ${clampY(y + strokeWidth2)} L${x1} ${y}`)
                        .setAttribute("stroke", "none")
                        .setAttribute("fill", this._colorAxes)
                        .setAttribute("name", "chevron-x-left")
                );

                if (xrange.x <= -1) {
                    this._lines.append(
                        GraphContainer
                            .create("text")
                            .setAttribute("fill", this._colorText)
                            .setAttribute("name", "label-axis-nx")
                            .setAttribute("dominant-baseline", "central")
                            .setAttribute("x", _x)
                            .setAttribute("y", clampY(_y - strokeWidth8))
                            .setAttribute("font-size", strokeWidth8)
                            .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                            .innerText("X'")
                    );
                }
            }
        }

        if (this._showAxisY) {
            const xval = THREE.Math.mapLinear(0, xrange.x, xrange.y, 0, this._size.width);

            const x = clampX(xval + this._origin.x);
            const y1 = clampY(this._origin.y), y2 = clampY(this._origin.y + this._size.height);

            const hasTop = this._showAxisArrowY === "both" || this._showAxisArrowY === "top";
            const hasBottom = this._showAxisArrowY === "both" || this._showAxisArrowY === "bottom";

            this._lines.append(
                GraphContainer
                    .create("line")
                    .setAttribute("x1", x)
                    .setAttribute("y1", clampY(y1 + (hasTop ? strokeWidth4 : 0)))
                    .setAttribute("x2", x)
                    .setAttribute("y2", clampY(y2 - (hasBottom ? strokeWidth4 : 0)))
                    .setAttribute("stroke-width", strokeWidth)
                    .setAttribute("stroke", this._colorAxes)
                    .setAttribute("name", "axis-y")
                    .setAttribute("pointer-events", "none"),
            );

            if (hasTop) {
                const _y = clampY(y1 + strokeWidth4);

                this._lines.append(
                    GraphContainer
                        .create("path")
                        .setAttribute("d", `M${x} ${y1} L${clampX(x - strokeWidth2)} ${_y} L${clampX(x + strokeWidth2)} ${_y} L${x} ${y1}`)
                        .setAttribute("stroke", "none")
                        .setAttribute("fill", this._colorAxes)
                        .setAttribute("name", "chevron-y-top")
                );

                if (yrange.x <= -1) {
                    this._lines.append(
                        GraphContainer
                            .create("text")
                            .setAttribute("fill", this._colorText)
                            .setAttribute("name", "label-axis-py")
                            .setAttribute("x", clampX(x + strokeWidth6))
                            .setAttribute("y", clampY(_y + strokeWidth6))
                            .setAttribute("font-size", strokeWidth8)
                            .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                            .innerText("Y")
                    );
                }
            }

            if (hasBottom) {
                const _y = clampY(y2 - strokeWidth4);

                this._lines.append(
                    GraphContainer
                        .create("path")
                        .setAttribute("d", `M${x} ${y2} L${clampX(x - strokeWidth2)} ${_y} L${clampX(x + strokeWidth2)} ${_y} L${x} ${y2}`)
                        .setAttribute("stroke", "none")
                        .setAttribute("fill", this._colorAxes)
                        .setAttribute("name", "chevron-y-bottom")
                );

                if (yrange.y >= 1) {
                    this._lines.append(
                        GraphContainer
                            .create("text")
                            .setAttribute("fill", this._colorText)
                            .setAttribute("name", "label-axis-ny")
                            .setAttribute("dominant-baseline", "central")
                            .setAttribute("x", clampX(x + strokeWidth6))
                            .setAttribute("y", clampY(_y - strokeWidth6))
                            .setAttribute("font-size", strokeWidth8)
                            .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                            .innerText("Y'")
                    );
                }
            }
        }

        return this;
    }
}

export default GraphGridlines;
export { GraphGridlines };